import { useState } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface DismissibleBannerProps {
  /** Key the dismissal is persisted under. Scope it per-banner — never share a key. */
  storageKey: string;
  /** Where dismissal persists. "session" (default) matches the beta banner — reappears next
   *  browser session. "local" persists across sessions. */
  storage?: "local" | "session";
  /** Banner body. The dismiss button is rendered separately, absolutely positioned over it. */
  children: React.ReactNode;
  dismissLabel?: string;
  /** Surface styling for the outer strip (background, border, text color, padding). */
  className?: string;
  /** Styling for the dismiss (X) button specifically — kept separate from `className` so each
   *  banner can match its own text color without the shell hardcoding one. */
  dismissButtonClassName?: string;
}

function getStorage(kind: "local" | "session"): Storage {
  return kind === "local" ? localStorage : sessionStorage;
}

/**
 * Full-width dismissible top strip with a persisted X dismissal. Extracted from the beta banner
 * that used to live inline in Layout.tsx.
 *
 * Follows the try/catch-guarded storage-read shape used by useSidebarCollapse.ts: storage access
 * can throw (private browsing, quota, disabled storage) and a banner failing to persist its
 * dismissal is not worth crashing the shell over.
 */
export function DismissibleBanner({
  storageKey,
  storage = "session",
  children,
  dismissLabel = "Dismiss banner",
  className,
  dismissButtonClassName,
}: DismissibleBannerProps) {
  const [visible, setVisible] = useState(() => {
    try {
      return getStorage(storage).getItem(storageKey) !== "true";
    } catch {
      return true;
    }
  });

  if (!visible) return null;

  const dismiss = () => {
    try {
      getStorage(storage).setItem(storageKey, "true");
    } catch {
      // Storage unavailable — dismissal just won't persist. Still hide for this render.
    }
    setVisible(false);
  };

  return (
    <div className={cn("shrink-0 w-full relative", className)}>
      {children}
      <button
        onClick={dismiss}
        className={cn(
          "absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md transition-colors",
          dismissButtonClassName
        )}
        aria-label={dismissLabel}
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
